// WHAT HAPPENS ON A SHOP'S FORM (TW-0131, 2026-09-08).
//
// Chris, mid-read, after we found that a client scanning a counter QR and not finishing was
// invisible on every instrument we had: "perhaps the form should beacon, wanna add that? could be
// good to know daily active users (i assume people just get on there, fill it out, and submit, but
// yeah)."
//
// He is almost certainly right about the assumption — 9,700 waivers say the form works — but until
// this file there was no way to KNOW, and the gap was the expensive kind: a trial shop with 0
// waivers read identically whether its counter QR was being scanned ten times a day and abandoned,
// or never scanned at all. Those are opposite problems with opposite fixes. Three instruments were
// checked first and all three are blind: the form beaconed nothing, the API's /shop Lambda logs
// carry no subdomain (START/END/REPORT only), and the recognition-rate table is a TTL rate-limiter
// whose rows expire within the hour.
//
// ⚠ THIS PAGE IS THE ONE SURFACE IN THE PRODUCT THAT HOLDS REAL CLIENT PII — a legal name, a date
// of birth, an address, a phone, an email, a photo of a driver's licence, and health answers. So
// the rule here is stricter than on the site or the shop app, and it is enforced STRUCTURALLY
// rather than by remembering:
//
//   * `track()` takes an event NAME from a closed union and an optional meta whose keys are a
//     closed union of five scalars. There is no path by which a form field can reach this module:
//     it is never handed the form state, and TypeScript refuses an unlisted key.
//   * `clean()` strips anything not on the allowlist at runtime too, because a `as any` cast
//     somewhere downstream would otherwise walk straight past the type.
//   * The API proxy (services/waiver-api/consolidated_api/form_beacon.py) strips it AGAIN on the
//     server, against the same list. Two independent filters, because one of them will eventually
//     be edited by someone who has not read this comment.
//
// THE UNIT IS ONE PAGE LOAD, not a browser and not a session. Every load mints a fresh `visit`,
// and nothing is persisted anywhere — no localStorage, no cookie, no id that survives the tab.
// That is the honest unit for a counter: a client scans, the page loads, they sign, they hand the
// phone back. A shop tablet doing five clients in a row is five loads and therefore five visits,
// which sessionStorage would have collapsed into one. It also means this file stores NOTHING about
// a person, which is the posture a consent form should have.
//
// AND IT CAN NEVER BREAK A SIGNING. Every entry point is wrapped, the transport is fire-and-forget,
// a failure is swallowed, and nothing here is ever awaited by the form. A shop losing a waiver
// because our telemetry threw would be a far worse bug than the blindness this fixes.

import { API_URL } from "./config";
import { isInternal } from "./internal";
import { isScripted } from "./scripted";
import { VIA_VALUES } from "./qr-tag";
import { REFUSAL_STEPS } from "./refusal";
import { currentSubdomain } from "./subdomain";

/** The events this page can report. A closed union: a new one is added HERE, deliberately, and the
 *  server's allowlist has to learn it too or it is dropped on arrival. */
export type FormEvent =
  /** the form painted with a real shop behind it — one per page load, the denominator for everything else */
  | "form_open"
  /** the client touched their first field: a scan that became an attempt */
  | "form_start"
  /** the client pressed the button and we posted */
  | "form_submit"
  /** the API answered 200: a signed waiver */
  | "form_done"
  /** the submission came back an error — the client saw a red message */
  | "form_error"
  /** the shop's free waivers are used up: the client met the paused screen, not the form */
  | "form_locked"
  // TW-0158 — the form ran with the trial banner above it (past the free ten, not yet at the wall)
  | "form_over_trial"
  /** this address has no shop behind it, or ours did not answer (TW-0097) */
  | "form_no_shop"
  /** the page threw and the client got a broken screen instead of a form (TW-0132) */
  | "form_js_error"
  /** THE FORM ITSELF STOPPED THE CLIENT, before anything was posted (TW-0129).
   *
   *  This is the blind spot the batch-photo wall lived in. A client-side refusal never reaches the
   *  API, so there is no 5xx, no `waivers-tattoo-api-5xx` alarm and no card — Texas forced a needle
   *  photo nobody could take for two days and the monitoring stayed silent the whole time, because
   *  nothing was ever posted. A server error is loud; a client stopped at the counter is silent, and
   *  the silent one is the one that loses the shop.
   *
   *  `meta.reason` is a FIXED CODE, never the message: the message can quote what the client typed. */
  | "form_refused"
  /** TW-0501 — THE CAMERA DID NOT OPEN on the photo screen (PhotoUploader.tsx): the browser refused it, or it was still dark
   *  after CAMERA_PATIENCE_MS. The ID photo is the refusal met most (579 refusals in 444 visits, 59 of them never signed, the
   *  form audit), and until this row nothing could say how many of those were a camera the browser kept from the page. `reason`
   *  is the browser's own reason as one word of CAMERA_FAILURES (never its message), `field` the photo it was for (its body
   *  key: `photoOfDriversLicense`), `step` where. Never a frame: nothing of the camera's reaches this module. */
  | "form_camera_failed"
  /** TW-0631 (2026-11-04) — THE PHOTO SCREEN, STEP BY STEP, for every photo the page asks for (`field`, its body key; `step`):
   *  the camera opened (`reason` retake when it replaced a kept photo), a photo kept (`reason` camera or library, the door it
   *  came in by), the camera closed with nothing kept, a kept photo cleared. The ID photo is the refusal met most, and in the
   *  week to Nov 4 three visits pressed Submit five or six times without one, with nothing to say whether they never opened
   *  the camera or closed it without Use Photo. Never a frame: nothing of the camera's reaches this module. */
  | "form_camera_opened"
  | "form_photo_kept"
  | "form_camera_closed"
  | "form_photo_cleared"
  /** TW-0501 — a draft in the tab was put back after a reload (draft.ts, TW-0500), once per load at most: the count that says
   *  whether the draft is worth its line. It carries `shop`, `lang` and `service`, never a value it put back. */
  | "form_draft_restored"
  /** TW-0628 (2026-11-04) — A PROTECTION THE SHOP'S OWNER TURNED OFF LET THE CLIENT THROUGH where the page would have stopped them
   *  (protections.ts): a pregnancy yes signing at a shop whose owner turned that refusal off with the risk accepted. A refusal's
   *  shape (`reason` the protection's id, `field` the question, `step`), once a visit per protection, and never a refusal. */
  | "form_protection_off"
  /** their address is one slip from a big provider and we said so (TW-0152) — the denominator for
   *  how often a client would have walked away with a waiver they could never be sent */
  | "email_typo_offered"
  /** they took the correction. Against `email_typo_offered` this is the only honest answer to
   *  whether the offer is worth the line it occupies */
  | "email_typo_taken"
  // ── WHAT THE PRINTED QR CODE OPENS (TW-0206) ────────────────────────────────────────────────
  // For a shop whose owner turned the choice on and whose deposit page is taking deposits, the page opens on Sign your
  // waiver · Pay a deposit (QrPicker.tsx). `form_open` still fires on that load as it always has (it counts loads of a
  // real shop's page); these three say what the choice did. Each carries `shop`, `lang` and `via`, nothing else.
  /** the choice was drawn instead of the form — once per load at most (picker.ts: it is only ever the first state) */
  | "picker_open"
  /** they tapped Sign your waiver: the form, on the same page */
  | "picker_waiver"
  /** they tapped Pay a deposit: off to the shop's deposit page */
  | "picker_deposit"
  // ── THE SHOP'S OWN PAGE FOR TEXTS (TW-0320 slice 2, 2026-10-17; texts.ts) ───────────────────────
  // `/texts` on the same host, for a shop that keeps another waiver system. `form_open` never fires there: these say what
  // the page did. Each carries `shop`, `lang` and `via` (which printed code brought the client: the texting QR's own words
  // are the waiver form's, `?qr` printed and `?qr=screen` on the shop app's screen, told apart from the form's by these
  // names), and a refusal its `reason` (the door's own code) and `status`. Never a name or a number: there is no key here for one.
  /** the page drew with the shop's sign-up open — once per load, the page's denominator */
  | "texts_open"
  /** the page drew and the shop's sign-up is not open, or the door did not answer (`reason`) — once per load */
  | "texts_closed"
  /** the client touched their first box: a scan that became an attempt */
  | "texts_start"
  /** the door answered yes */
  | "texts_done"
  /** the door said no, or did not answer: `reason` is its code (`bad_number`, `rate_limited`, `unreachable`…) */
  | "texts_refused"
  // ── THE DEMO SHOP ONLY (TW-0135) ──────────────────────────────────────────────────────────
  //
  // Chris: "we likely should add this to the beacon on this page. well the demo form is slightly
  // different than a shop's form." It is: nobody signs anything here, and what a visitor DOES is
  // configure a fake shop to look like theirs. Which state they reach for is the sales question
  // this page can answer and nothing else can — a shop owner who set it to Georgia and left is a
  // Georgia shop that looked. Every one of these carries `shop: "demo"`, so a read can separate
  // them from the 9,700 real signings with the field it already has.
  /** a dial moved; the meta carries the WHOLE configuration it moved to, so one row is one answer */
  | "demo_dial"
  /** they asked for the QR code — the moment a laptop visit tries to become a phone visit */
  | "demo_qr"
  /** …and it became one: the phone that scanned that code opened it, and the laptop said so (TW-0136) */
  | "demo_paired"
  /** they put it all back */
  | "demo_reset";

/** The ONLY keys that may ride an event. Every one is a fixed vocabulary or a small integer; not
 *  one of them can carry a character the client typed. */
type Meta = {
  /** the shop's subdomain — a shop, never a person */
  shop?: string;
  /** "en" | "es" — which language the page was showing. ⚠ It never arrives as sent: the intake stamps `lang` from the
   *  browser's Accept-Language header over it (see `formLang` below), so on a stored row `lang` is the BROWSER's language */
  lang?: string;
  /** "tattoo" | "piercing" — which form, for a shop that offers both */
  service?: string;
  /** true when this browser is marked as one of ours (internal.ts), so the read can exclude us */
  internal?: true;
  /** true when a script drives this browser (scripted.ts: navigator.webdriver), stamped by row() beside `internal`, the
   *  site's two marks the same way, so a read names a walk a script and never a client at the counter (2026-10-18) */
  scripted?: true;
  /** the HTTP status behind a form_error — a number, never the message, which can quote input */
  status?: number;
  /** WHY the form refused a submission (form_refused) — one of Form.tsx's own fixed codes, never
   *  the message shown to the client, which is assembled from the shop's name and can quote input */
  reason?: string;
  // ── the demo's dials (TW-0135). Each is a value PICKED from a fixed list, never typed: the
  // state is one of 51 two-letter codes, the type one of three words, and minors is a boolean.
  // They are as safe as `service` is and unsafe in no new way — there is still no path from a
  // form field to this module.
  /** the demo's state dial: two letters, or absent for "any state" */
  demoState?: string;
  /** the demo's shop-type dial: tattoo · piercing · both */
  demoType?: string;
  /** the demo's minors switch */
  demoMinors?: true;
  // ── WHAT A CRASH CARRIES (TW-0132). These four are the only keys on this list that are not a
  // fixed vocabulary, and `message` is the one honest risk on the whole page: an Error THIS bundle
  // throws could interpolate a value if somebody wrote it that way. None does today (checked
  // 2026-09-09: no Error thrown anywhere in apps/form/src with a value interpolated into it), and
  // standing rule for anyone adding one is that a field value never goes in the message. What the
  // machine enforces instead is the cap, the scrub in `redact()`, and the second filter on the
  // server.
  /** the error's own message — the first 300 characters of it */
  message?: string;
  /** the stack, or React's component stack for a boundary catch — 1500 characters */
  stack?: string;
  /** WHICH ROUTE, as a pattern and never as a URL: `routeOf()` maps the three named routes and
   *  answers "other" for the rest, so a real `waiver_unique_id` has no path here */
  route?: string;
  /** window.onerror | unhandledrejection | route | boundary — how we heard about it */
  source?: string;
  /** WHICH PRINTED CODE THIS SCAN CAME FROM (TW-0190): "qr" for a placard, "mail" for a card we
   *  posted, absent for the thousands printed before either tag existed. A closed vocabulary, read
   *  off the URL by qr-tag.ts and checked again here — nobody types it, but anybody can put
   *  anything after `?qr=`, which is the same reason `route` is checked rather than capped. */
  via?: string;
  /** WHICH DOOR OPENED THE DEMO (2026-09-23): the HOST the demo page was opened from — `www.waivers.tattoo`,
   *  `www.google.com`, `l.instagram.com` — read off `document.referrer` by referrerHost() and cut at the host,
   *  never a path, never a query. THE DEMO ONLY: App.tsx passes it for the demo shop alone, and the server keeps
   *  it only on a `demo` row (form_beacon.py), so a real shop's form sends exactly what it always has. A
   *  hostname is not client data, and on the demo there is no client. The day it was built, 2 of 5 signups had
   *  arrived through the demo and nothing on either side could say how they had reached it. */
  from?: string;
  // ── WHAT A REFUSAL CARRIES (2026-10-26; the read is `./run.sh refusals`, scripts/lib_refusals.py) ─────────────────────
  // Chris, that morning, after a shop's "their ID is not valid" turned out to be the form's own words and the phone box
  // refusing a number typed as dialed at home, found by hand off this beacon and the API's CloudWatch: "we should be
  // beaconing refusals and seeing if we have defects." Until this day a refusal left as its code and nothing else: never
  // WHICH clause or question, never where on the page, never the shop's state, never the language the page was in, and
  // the API's own refusals as a bare status. Each key below is a word of OURS or a closed shape; none can hold what a
  // client typed, and each is checked raw here and again on the server (form_beacon.py), dropped rather than repaired.
  /** WHICH BOX OR QUESTION: the input's own name, or our key for the question or the clause (`phoneNumber`,
   *  `photograph`, `oq_b4e20a8742`), never what is in it. A letter first, then letters, digits and `_ . : -`, 64 at most */
  field?: string;
  /** WHERE ON THE PAGE: the section the refused question sits in, one word of REFUSAL_STEPS (refusal.ts), and `door` for
   *  a refusal the API's signing door answered after the press */
  step?: string;
  /** THE SHOP's state, two capitals off the shop's own row (never the client's address): every rule a form asks follows it */
  state?: string;
  /** THE LANGUAGE THE FORM WAS SHOWING, "en" or "es". Never `lang`: the intake stamps `lang` from the browser's own
   *  Accept-Language header over whatever the page sent (the admin's stamp_header, FL-1548), so the page's own `lang` has
   *  never arrived: measured 2026-10-26, every form row of the week held a header (`en-US,en;q=0.9`), none an "es" */
  formLang?: string;
  /** THE API's OWN CODE for a refusal at the signing door (`PHONE_NOT_VALID`), read off its answer: capitals, digits and
   *  underscores, 64 at most. Never its sentence, which names the shop and is in the client's language */
  code?: string;
};

// ⚠ ORDER IS LOAD-BEARING: services/waiver-api/tests/unit/test_form_beacon.py PARSES this literal
// and asserts tuple equality with the server's own META_KEYS, so the two lists cannot drift
// quietly. Append here and there in the same commit. Keep it values-only — that test reads to the
// first closing bracket, so a comment inside the array would become a key.
const ALLOWED: (keyof Meta)[] = [
  "shop", "lang", "service", "internal", "status", "demoState", "demoType", "demoMinors",
  "message", "stack", "route", "source", "via", "from", "reason", "scripted", "field", "step", "state", "formLang", "code",
];

/** A REFUSAL'S CODE: the first argument of one of Form.tsx's own `refuse("…")` calls, lowercase letters and
 *  underscores, 64 at most. Form.test.tsx holds every call site to the same shape (`refuse\("[a-z_]+",`), so every
 *  code the form can send passes, and a sentence, an address or anything a client typed cannot pass for one. The
 *  server checks the same pattern (form_beacon.py `REASON`). */
const REASON_CODE = /^[a-z_]{1,64}$/;

/** A REFUSAL'S BOX OR QUESTION (2026-10-26): an input's name or our key, a letter first. The server checks the same shape
 *  (form_beacon.py `FIELD`). A phone number starts with a digit and an address holds a space or an @, so neither passes. */
const FIELD_SHAPE = /^[A-Za-z][A-Za-z0-9_.:-]{0,63}$/;
/** THE API's REFUSAL CODE (2026-10-26): `PHONE_NOT_VALID`'s shape; the server's form_beacon.py `CODE` is the same. */
const API_CODE = /^[A-Z][A-Z0-9_]{0,63}$/;
/** THE SHOP's STATE: two capitals, the shape every shop row's `state` has. */
const STATE_CODE = /^[A-Z]{2}$/;
/** The two languages a form is shown in (i18n.ts LANGS). */
const FORM_LANGS = ["en", "es"];

/** A stack frame names the file it ran in, and for a bundled or inline frame that file is the PAGE.
 *  Measured on the site's own error rows, 2026-09-09: frames read
 *  `https://www.waivers.tattoo/compliance/new-jersey:1:4222`. On THIS host a page can be
 *  `/w/<waiver_unique_id>` — a working link to one client's signed waiver, holding a legal name, a
 *  date of birth, an address and health answers. So the id comes out of every string that leaves
 *  here, and it comes out BEFORE the length cap: capping first leaves half an id, which is still
 *  half a secret and no longer matches this pattern. */
const WAIVER_LINK = /\/w\/[^\s/?#"'`)>\]]+/g;

/** `/w/:id` is what it substitutes AND what `routeOf()` returns, so scrubbing a route is a no-op
 *  rather than a mangle. */
function redact(value: string): string {
  return value.replace(WAIVER_LINK, "/w/:id");
}

/** How long each key may be. The fixed vocabularies get 64; the two that carry prose get their
 *  own, because a stack cut at 64 characters names one frame and answers nothing. */
const CAPS: Partial<Record<keyof Meta, number>> = { message: 300, stack: 1500 };
const MAX_STR = 64;

/** WHICH SCREEN, as one of five answers — never `window.location.pathname`, which on `/w/:id` IS
 *  the secret. Anything unrecognised is "other" rather than itself: a route this function has not
 *  learned is worth less than the guarantee that no id can ride under this key. `/texts` joined on
 *  2026-10-17 with the shop's own page for texts (TW-0320, texts.ts), so a crash there says where. */
export function routeOf(pathname?: string): string {
  let path = pathname;
  if (path === undefined) path = typeof window === "undefined" ? "" : window.location.pathname;
  if (path === "" || path === "/") return "/";
  if (path === "/terms-and-conditions") return "/terms-and-conditions";
  if (path === "/texts" || path === "/texts/") return "/texts";
  // the router's `/w/:waiver_unique_id`, written short — see `redact()` for why it is this string
  if (/^\/w\/[^/]+\/?$/.test(path)) return "/w/:id";
  return "other";
}

/** Strip everything not on the allowlist, and coerce what is left to a scalar. The types already
 *  say this; a cast anywhere downstream would walk past the types, and this is what stands then. */
function clean(meta: Meta | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!meta) return out;
  for (const k of ALLOWED) {
    const v = (meta as Record<string, unknown>)[k];
    if (v === undefined || v === null) continue;
    if (k === "status") {
      const n = Number(v);
      if (Number.isFinite(n)) out[k] = Math.trunc(n);
      continue;
    }
    if (k === "internal" || k === "demoMinors" || k === "scripted") {
      if (v === true) out[k] = true;
      continue;
    }
    if (k === "via") {
      // A CLOSED VOCABULARY, checked against qr-tag.ts's own list rather than a second one here, the
      // way `route` is checked against `routeOf`. Anything else is dropped rather than capped: the
      // value comes off a URL a stranger can type, and a 64-character free string on a row that rides
      // to the shared analytics intake is exactly what this file exists to not have.
      if (VIA_VALUES.includes(`${v}`)) out[k] = `${v}`;
      continue;
    }
    if (k === "from") {
      // A HOSTNAME OR NOTHING: letters, digits, dots and dashes, 64 at most, lowercased. referrerHost() has
      // already cut the referrer at its host; this is the second cut, for a caller that did not. A value that
      // is not a hostname is dropped, never repaired — it came off the browser, not out of our code.
      const h = `${v}`.trim().toLowerCase();
      if (/^[a-z0-9.-]{1,64}$/.test(h)) out[k] = h;
      continue;
    }
    if (k === "reason") {
      // WHY THE FORM REFUSED (after the payment-method line, 2026-09-23). On the Meta type since TW-0129 and on
      // this list only now: until today TypeScript accepted `reason` at the one call site and this function dropped
      // it at every send, so a refusal left as "a refusal" and never as which one. A string that is already a code,
      // or nothing: checked RAW like `via`, and dropped rather than repaired, because the codes are ours and a value
      // that is not one is not a code, least of all the message beside it, which can quote what the client typed.
      if (typeof v === "string" && REASON_CODE.test(v)) out[k] = v;
      continue;
    }
    if (k === "field" || k === "code" || k === "state") {
      // WHICH BOX, THE API's CODE, THE SHOP's STATE (2026-10-26): each a word of ours in a closed shape, checked RAW like
      // `reason` and dropped rather than repaired, because a value that is not already one is not one
      const shape = k === "field" ? FIELD_SHAPE : k === "code" ? API_CODE : STATE_CODE;
      if (typeof v === "string" && shape.test(v)) out[k] = v;
      continue;
    }
    if (k === "step") {
      // WHERE ON THE PAGE: one word of refusal.ts's own list, the way `via` is checked against qr-tag.ts's
      if (typeof v === "string" && (REFUSAL_STEPS as readonly string[]).includes(v)) out[k] = v;
      continue;
    }
    if (k === "formLang") {
      if (typeof v === "string" && FORM_LANGS.includes(v)) out[k] = v;
      continue;
    }
    if (k === "route") {
      // Checked RAW, against `routeOf` itself rather than a second list to keep in step: a value
      // that is already a pattern is its own answer, and anything else — a real path, a URL — is
      // dropped rather than scrubbed into looking like one. The server says the same sentence
      // (form_beacon.py `_clean_meta`), which is the only way two filters catch each other.
      if (routeOf(`${v}`) === `${v}`) out[k] = `${v}`;
      continue;
    }
    // shop / lang / service / source: short fixed vocabularies, where the cap is belt-and-braces.
    // message / stack are the two that carry prose and get their own. The scrub runs on EVERY
    // string rather than on a list of the risky ones, so a key added later inherits it instead of
    // needing somebody to remember.
    out[k] = redact(`${v}`).slice(0, CAPS[k] ?? MAX_STR);
  }
  return out;
}

/** A random id for THIS page load. Not stored anywhere: when the tab goes, so does it. */
function mintVisit(): string {
  try {
    const c = typeof crypto !== "undefined" ? crypto : undefined;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
    if (c && typeof c.getRandomValues === "function") {
      const a = new Uint8Array(16);
      c.getRandomValues(a);
      return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch {
    /* fall through to Math.random, which is plenty for a telemetry grouping key */
  }
  return `v${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

const visit = mintVisit();

/** Overridable for tests; the real one is a POST the browser need not wait for. */
type Send = (url: string, body: string) => void;

const browserSend: Send = (url, body) => {
  try {
    // text/plain keeps it a "simple" request — no CORS preflight, which matters because this
    // bundle is served from every *.waivers.tattoo and the API is a different origin from all of
    // them. The proxy reads the raw body and does not care what we called it.
    const blob = typeof Blob !== "undefined" ? new Blob([body], { type: "text/plain" }) : null;
    if (blob && typeof navigator !== "undefined" && navigator.sendBeacon) {
      if (navigator.sendBeacon(url, blob)) return;
    }
    // `keepalive` so a beacon fired as the page goes away still leaves. The `.catch` is
    // load-bearing: an unhandled rejection here becomes a console error on a client's phone.
    void fetch(url, { method: "POST", body, keepalive: true, mode: "no-cors" }).catch(() => {});
  } catch {
    /* telemetry never breaks a signing */
  }
};

/** ⚠ UNDER THE TEST RUNNER, NOTHING LEAVES (2026-10-26). jsdom has no sendBeacon, and the CRA test setup's fetch is a
 *  polyfill over jsdom's XMLHttpRequest, which makes REAL requests; a test bundle's API_URL is the deployed one. So every
 *  test that rendered the form without handing in a transport POSTED ITS EVENTS TO PRODUCTION: measured off the beacon on
 *  the morning of the read of refusals, 5,150 rows in 61 days from `Mozilla/5.0 (darwin) ... jsdom/16.7.0`, every one
 *  on the demo shop (4,285 `form_refused`, 865 `form_start`, no `form_open`), which is the "29 opens against 463 starts"
 *  the once-per-visit note below could not reproduce (TW-0149). CRA inlines NODE_ENV at build, so a real bundle reads
 *  "production" here and keeps the browser's transport; a test that means to exercise it asks for `browserTransport`. */
const defaultSend: Send = process.env.NODE_ENV === "test" ? () => {} : browserSend;

let send: Send = defaultSend;

/** Tests only: the browser's own transport, which a test must ask for by name (`setSend(browserTransport)`). */
export const browserTransport: Send = browserSend;

/** Tests hand in their own transport; `null` restores the default (the browser's, and nothing under the test runner). */
export function setSend(fn: Send | null): void {
  send = fn ?? defaultSend;
}

/** ⚠ DELIBERATELY EMPTY — see the long note beside `ERROR_NAMES` in the API's form_beacon.py.
 *  `kind: "error"` arms funeral.link's ErrorWatch, which is enabled in prod, ticks every 15
 *  minutes, mints an unowned p1 card per defect and pages Chris by email — and its title is built
 *  from the row's NAME, which here is the constant "form_js_error". So a form crash would card and
 *  email the OTHER business's board carrying none of the actual error. The rows ride as `custom`
 *  and stay fully readable; adding the name back here is all it takes to arm it on purpose. */
const ERROR_NAMES: FormEvent[] = [];

/** The wire row, exported so the test can assert on the exact shape the intake is given. */
export function row(name: FormEvent, meta?: Meta): Record<string, unknown> {
  const m = clean(meta);
  // OURS (2026-10-18, TW-0352 item 12): a browser marked by hand (internal.ts), or one a script drives (scripted.ts), which
  // carries `scripted` beside `internal`, the way the site stamps its rows (apps/marketing/lib/beacon.ts flush). Stamped here,
  // the one door every event passes, so a walk of a real shop's form is never counted as a client at its counter.
  const scripted = isScripted();
  if (isInternal() || scripted) m.internal = true;
  if (scripted) m.scripted = true;
  return {
    kind: ERROR_NAMES.includes(name) ? "error" : "custom",
    name,
    property: "waivers_tattoo",
    // The path is the SHOP's page, and it is written from the shop we were given rather than read
    // from window.location, which on this host would be the bare "/" of a wildcard CDN.
    path: m.shop ? `/form/${m.shop}` : "/form",
    session_ref: visit,
    occurred_at: new Date().toISOString(),
    meta: m,
  };
}

/* THE TWO EVENTS THAT MEAN "ONCE THIS VISIT", ENFORCED HERE (TW-0149).
 *
 * `form_open` is the DENOMINATOR — one per page load, said in this file's own header — and
 * `form_start` is "this scan became an attempt", which is likewise a fact about the visit and not
 * about a React tree. Both were guarded in the COMPONENT that fires them: `form_open` by a
 * `useEffect` in App, `form_start` by a `startedRef` in Form. A component-scoped guard is only as
 * durable as the mount, and the mount is not the unit either of these events counts in.
 *
 * MEASURED ON PROD, 2026-09-09: the demo shop reported 29 opens and 463 STARTS over 30 days, 218 of
 * them in the last day alone — a form STARTED eight times for every time it was OPENED, which the
 * two events cannot both mean. I could not reproduce it: driving the live demo through every dial,
 * every checkbox, the language switch, the customize panel and a terms round-trip produced exactly
 * one open and one start every time. So the mechanism is still unknown, and this fix deliberately
 * does not depend on knowing it — whatever path re-enters the form without a new page load, the
 * count is now right, because the guard is finally in the same scope as the thing it counts.
 *
 * WHY THIS IS NOT PAPERING OVER A BUG. If a real remount is happening it is worth finding, and the
 * `visits` count on the admin's demo block is what will show it. But `form_start` firing twice in
 * one visit is not a second attempt under any reading, so a second row was never data we lost — it
 * was data that was wrong.
 *
 * Everything else stays repeatable on purpose: `form_error` can fire per failed submit,
 * `demo_dial` per dial, and a shop tablet doing five clients in a row is still five visits and
 * therefore five opens (a new page load mints a new `visit`, and this set is per-module and
 * therefore per-load with it).
 */
const ONCE_PER_VISIT: FormEvent[] = ["form_open", "form_start", "texts_open", "texts_closed", "texts_start", "form_draft_restored"];
const fired = new Set<FormEvent>();

/** Report one thing that happened on a shop's form. Never throws, never blocks, never awaited. */
/** The host this page was opened from, for the demo's `from` — never the path, never the query. The page's
 *  own host (a reload, a navigation inside the form) and an empty referrer both read as nothing. */
export function referrerHost(): string | undefined {
  try {
    const r = typeof document === "undefined" ? "" : document.referrer;
    if (!r) return undefined;
    const h = new URL(r).host.toLowerCase();
    return h && h !== window.location.host.toLowerCase() ? h : undefined;
  } catch {
    return undefined;
  }
}

export function track(name: FormEvent, meta?: Meta): void {
  try {
    if (ONCE_PER_VISIT.includes(name)) {
      if (fired.has(name)) return;
      fired.add(name);
    }
    send(`${API_URL}/beacon`, JSON.stringify({ events: [row(name, meta)] }));
  } catch {
    /* a signing is worth more than a data point */
  }
}

/** Tests only — a module-level set outlives a test, and a suite that shares one would pass or fail
 *  by file order. Never called by the page: a real visit ends when the tab does. */
export function resetVisitGuard(): void {
  fired.clear();
}

// ── THE PRESS LEAVES WITH ITS ANSWER (TW-0501) ────────────────────────────────────────────────────────────────────────────
//
// The form audit (docs/FORM-AUDIT-2026-10-26.md § 12): 355 of 6,702 visits that signed carried no `form_submit`, 5.3%. Read
// again off this beacon for 2026-10-23 to 10-26, it is a phone's and nobody else's: 61 of 608 visits that signed on an iPhone
// (10%) had no press, and not one of the 190 that signed on anything else. It is not the photo library (the 61 chose a saved
// ID photo as often as the rest, 5 in 53 against 34 in 397, off the rows' own `idPhotoSource`), nor how long the page sat
// before the press (a median 168 seconds against 189). What the 61 share: each signed on its FIRST press, and not one of the
// iPhone visits whose press was refused first lost the press that signed (0 of 87). So on those phones the first beacon sent
// from the press could be lost while the open, the first touch and the `form_done` after the answer all arrived.
//
// So the press no longer leaves on its own. Its row is made AT the press (its `occurred_at` is the press) and held, and it
// leaves in the SAME request as the answer's row, `form_done` or `form_error`, which is the request that arrives: a signing
// that is counted carries its press, whatever the phone did with a beacon of its own. A press with no answer leaves alone,
// when the page is hidden or closed, or HOLD_MS after the press, whichever comes first, and never twice. The read is
// unchanged: one `form_submit` per press, at the instant of the press.

/** A press with no answer yet leaves on its own this long after it (the press takes 3 seconds at the median, 8 at the 90th,
 *  7.9 on a fast-3G line with a full-size photo: the form audit's Measured table). */
export const HOLD_MS = 20000;

/** A row made now and sent with what it waits for. */
export type Held = {
  /** the held row and the answer's row in one request; the answer alone when the held row has already left */
  withAnswer: (name: FormEvent, meta?: Meta) => void;
};

/** Send rows in one request. Never throws. */
function post(rows: Record<string, unknown>[]): void {
  try {
    if (rows.length) send(`${API_URL}/beacon`, JSON.stringify({ events: rows }));
  } catch {
    /* a signing is worth more than a data point */
  }
}

/** Hold the press (TW-0501): its row now, sent with the answer, or alone if none comes. Never throws, never blocks. */
export function hold(name: FormEvent, meta?: Meta, waitMs: number = HOLD_MS): Held {
  let held: Record<string, unknown> | null = null;
  try {
    held = row(name, meta);
  } catch {
    held = null;
  }
  let timer: ReturnType<typeof setTimeout> | null = null;
  const take = (): Record<string, unknown> | null => {
    const out = held;
    held = null;
    if (timer) clearTimeout(timer);
    timer = null;
    try {
      window.removeEventListener("pagehide", alone);
      document.removeEventListener("visibilitychange", whenHidden);
    } catch {
      /* no window: nothing was listening */
    }
    return out;
  };
  function alone(): void {
    const out = take();
    if (out) post([out]);
  }
  function whenHidden(): void {
    if (document.visibilityState === "hidden") alone();
  }
  try {
    timer = setTimeout(alone, waitMs);
    window.addEventListener("pagehide", alone);
    document.addEventListener("visibilitychange", whenHidden);
  } catch {
    /* no window (a test of the module alone): the press still leaves with its answer */
  }
  return {
    withAnswer: (answer, answerMeta) => {
      try {
        const out = take();
        const said = row(answer, answerMeta);
        post(out ? [out, said] : [said]);
      } catch {
        /* a signing is worth more than a data point */
      }
    },
  };
}

// ── WHEN THE PAGE BREAKS ON A CLIENT'S PHONE (TW-0132) ────────────────────────────────────────
//
// Chris, 2026-09-08, having found `/w/:waiver_unique_id` throwing on every waiver ever signed:
// "likely need to card this failure as something that should have auto-created a card, that's a
// critical error we never would have seen" · "i haven't looked at this screen in literally years".
// 163 form tests passed the whole time; none opened that route, and no instrument on this bundle
// could say a page had thrown. The site has had this rail for days — this IS that rail, copied
// rather than redesigned, because the copy is the one that has been reviewed and run.
//
// AN ERRORING PAGE ERRORS AGAIN, AND THE SECOND ONE IS FREE. A render loop, a handler that
// rethrows, a bad state re-firing on every keystroke: one page load can produce errors as fast as
// the browser runs, and each is a POST from a phone on shop wifi. 20 distinct errors from one visit
// already say everything a read needs, so the 21st is dropped on the floor.
let errorsThisLoad = 0;

/** The shop whose page broke, stamped HERE rather than at each call site — a choke point cannot be
 *  forgotten by the next reporter somebody adds. It is the hostname (subdomain.ts), which is known
 *  at module load, before any fetch has answered. */
function shopOfThisPage(): string | undefined {
  try {
    return currentSubdomain() || undefined;
  } catch {
    return undefined;          /* no window: a test, a server render — report it without a shop */
  }
}

/** Report a crash. Never throws, never blocks, capped per page load. */
export function trackError(
  message: string,
  extra?: { source?: string; stack?: string; route?: string }
): void {
  if (errorsThisLoad >= 20) return;
  errorsThisLoad += 1;
  try {
    track("form_js_error", {
      shop: shopOfThisPage(),
      message: message || "error",
      stack: extra?.stack,
      route: extra?.route ?? routeOf(),
      source: extra?.source,
    });
  } catch {
    /* a signing is worth more than a crash report about a signing */
  }
}

let installed = false;

/** `window.onerror` + `unhandledrejection` → the same rail. Called ONCE from src/index.tsx at
 *  module load rather than from a screen: a crash while the bundle is still booting happens before
 *  React exists, which is exactly the crash an error boundary cannot see, and installing here
 *  covers every route — including the two nobody thinks about, `/w/:id` and the 404.
 *
 *  The site's version also records `at`: the failing file, line and column. This one does not, on
 *  purpose. On THIS host that filename can be the page, and the page can be `/w/<id>`. The stack
 *  carries the same frames and goes through `redact()`; a second key holding the same URL would
 *  just be a second thing to remember to scrub. */
export function installErrorHandlers(): void {
  if (typeof window === "undefined" || installed) return;
  installed = true;
  window.addEventListener("error", (e) => {
    const stack = (e.error as { stack?: unknown } | undefined)?.stack;
    trackError(e.message || "window_error", {
      source: "window.onerror",
      stack: typeof stack === "string" ? stack : undefined,
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = (e as PromiseRejectionEvent).reason as { message?: unknown; stack?: unknown } | undefined;
    trackError(
      typeof reason?.message === "string" && reason.message ? reason.message : "unhandled_rejection",
      { source: "unhandledrejection", stack: typeof reason?.stack === "string" ? reason.stack : undefined }
    );
  });
}
